/**
 * How long to wait for the browser to work out a file's duration.
 *
 * A metadata load is the first few hundred KB of the file; a minute covers
 * that on any connection worth playing over. Without a limit the promise
 * waited for ever on an element whose load the browser had quietly dropped -
 * a sleeping tab does that - and the request queue slot it held went with it.
 */
export const mediaDurationTimeoutMs = 60 * 1000;

export class MediaDurationTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`No duration after ${Math.round(timeoutMs / 1000)}s`);
    this.name = 'MediaDurationTimeoutError';
  }
}

/**
 * https://github.com/captbaritone/webamp/blob/master/js/fileUtils.ts
 * @param url
 */
export function genMediaDuration(url: string, timeoutMs = mediaDurationTimeoutMs): Promise<number> {
  if (typeof url !== 'string') {
    throw new Error('Attempted to get the duration of media file without passing a url');
  }

  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    audio.crossOrigin = 'anonymous';
    // TODO: Does this actually stop downloading the file once it's
    // got the duration?
    audio.preload = 'metadata';

    /**
     * Whichever of the three outcomes comes first wins; the listeners go so
     * the others cannot follow, and an empty src runs the element's load
     * algorithm, which aborts whatever it was still fetching.
     */
    function settle() {
      clearTimeout(timer);
      audio.removeEventListener('durationchange', durationChange);
      audio.removeEventListener('error', onError);
      audio.src = '';
    }

    function durationChange() {
      const duration = audio.duration;
      settle();
      resolve(duration);
    }

    function onError(e: Event) {
      settle();
      reject(e);
    }

    const timer = setTimeout(function mediaDurationTimeout() {
      settle();
      reject(new MediaDurationTimeoutError(timeoutMs));
    }, timeoutMs);

    audio.addEventListener('durationchange', durationChange);
    audio.addEventListener('error', onError);
    audio.src = url;
  });
}
